import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Building2, Shovel, MapPin, Building, 
  ArrowRight, Shield, FileCheck, Users, Phone
} from 'lucide-react';

const HERO_IMAGE = "https://images.pexels.com/photos/35372560/pexels-photo-35372560.jpeg";

const ServiceCard = ({ icon: Icon, title, description, link, delay }) => (
  <Card 
    className={`group card-hover bg-card border-none rounded-2xl overflow-hidden animate-fade-in-up opacity-0 stagger-${delay}`}
    data-testid={`service-card-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2 font-serif">{title}</h3>
      <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{description}</p>
      <Link to={link}>
        <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 group/btn">
          En savoir plus 
          <ArrowRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const FeatureItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-secondary" />
    </div>
    <div>
      <h4 className="font-semibold mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  </div>
);

export default function Home() {
  const services = [
    {
      icon: Shovel,
      title: "Terrassements",
      description: "Investissez dans un lotissement et recevez des terrains proportionnels à votre apport. Exemple : un apport de 5M FCFA peut vous rapporter une parcelle de 300m².",
      link: "/terrassements"
    },
    {
      icon: Building2,
      title: "Coopératives",
      description: "Accédez à la propriété avec un acompte de 25% et le solde réparti sur 15 à 20 mensualités. Documents juridiques remis à la fin du paiement.",
      link: "/cooperatives"
    },
    {
      icon: MapPin,
      title: "Sites & Parcelles",
      description: "Trouvez le terrain idéal parmi nos sites à Yenne, Diamniadio, Saly et plus. Photos, géolocalisation et documents juridiques disponibles.",
      link: "/sites"
    },
    {
      icon: Building,
      title: "Logements",
      description: "Location, vente ou location-vente : découvrez nos appartements et villas. Exemple : T3 de 65m² à partir de 350 000 FCFA/mois.",
      link: "/logements"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Sécurisation documentaire",
      description: "Tous nos terrains disposent de documents juridiques vérifiés"
    },
    {
      icon: FileCheck,
      title: "Accompagnement complet",
      description: "De la visite à la signature, nous vous guidons à chaque étape"
    },
    {
      icon: Users,
      title: "Mise en relation notaire",
      description: "Partenariat avec des notaires de confiance pour vos transactions"
    },
    {
      icon: Phone,
      title: "Visites guidées",
      description: "Planifiez une visite gratuite avec nos agents sur le terrain"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[32vh] min-h-[240px] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/85 to-foreground/50" />
        </div>
        
        <div className="relative w-full px-6 lg:px-12 py-6">
          <div className="max-w-6xl">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 font-serif animate-fade-in-up">
              Votre Avenir Immobilier au <span className="text-secondary">Sénégal</span>
            </h1>
            <p className="text-sm sm:text-base text-white/80 mb-4 leading-relaxed animate-fade-in-up stagger-1 max-w-3xl">
              JAMM IMMO vous accompagne dans tous vos projets : terrassements, coopératives, terrains et logements. Investissez en toute confiance.
            </p>
            <div className="flex flex-wrap gap-3 animate-fade-in-up stagger-2">
              <Link to="/sites">
                <Button size="default" className="btn-pill bg-primary hover:bg-primary/90">
                  Découvrir nos sites
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="default" variant="outline" className="btn-pill border-white text-white hover:bg-white/10">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-serif">Nos Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Des solutions adaptées à chaque projet immobilier, de l'investissement à l'acquisition
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} delay={index + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-muted/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">
                Pourquoi Choisir <span className="text-primary">JAMM IMMO</span> ?
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Avec plus de 10 ans d'expérience dans l'immobilier au Sénégal, 
                nous avons accompagné des centaines de familles vers la propriété. 
                Notre expertise locale et notre réseau nous permettent de vous 
                proposer les meilleures opportunités.
              </p>
              
              <div className="grid gap-6">
                {features.map((feature) => (
                  <FeatureItem key={feature.title} {...feature} />
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5853835/pexels-photo-5853835.jpeg"
                alt="Famille heureuse"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-card p-6 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-primary font-serif">500+</div>
                <div className="text-sm text-muted-foreground">Familles accompagnées</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">
            Prêt à Investir ?
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8">
            Contactez-nous dès aujourd'hui pour découvrir nos opportunités 
            d'investissement et commencer votre projet immobilier.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90">
                Demander un devis
              </Button>
            </Link>
            <a href="tel:+221771787379">
              <Button size="lg" variant="outline" className="btn-pill border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                <Phone className="w-5 h-5 mr-2" />
                +221 77 178 73 79
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
